import { useCallback, useEffect, useMemo } from 'react';
import { pixKeyTypeEmail, pixKeyTypePhone, pixKeyTypeRandom, pixKeyTypeTaxIdentifier } from '@ibk/shared-assets';
import { useTranslation } from '@ibk/shared-translation';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { PixType } from '../../enums';
import { formatPixKey, formatPixKeyAlphanumeric, PixType as FormatPixType } from '../../utils/formatPixKey';
import { usePixKeyDelete } from '../usePixKeyDelete';
import { usePixKeys } from '../usePixKeys';
export function usePixKeyList() {
    const { t } = useTranslation();
    const { isValidCnpj, maskCnpj, unmaskCnpj } = useCnpjAlphanumeric();
    const { pixKeys, loading, error, getPixKeys } = usePixKeys();
    const { loading: deleteLoading, error: deleteError, handleDeletePixKey, stopLoading } = usePixKeyDelete();
    useEffect(() => {
        if (!pixKeys) {
            getPixKeys();
        }
    }, [getPixKeys, pixKeys]);
    const hasKeys = Boolean(pixKeys && pixKeys.length > 0);
    const typeMap = useMemo(() => ({
        [PixType.EMAIL]: {
            label: t('pix-keys.email'),
            icon: pixKeyTypeEmail,
            formatType: FormatPixType.EMAIL,
        },
        [PixType.TAX_IDENTIFIER]: {
            label: t('pix-keys.cpf-cnpj'),
            icon: pixKeyTypeTaxIdentifier,
            formatType: FormatPixType.TAX_IDENTIFIER,
        },
        [PixType.PHONE]: {
            label: t('pix-keys.phone'),
            icon: pixKeyTypePhone,
            formatType: FormatPixType.PHONE,
        },
        [PixType.RANDOM]: {
            label: t('pix-keys.random'),
            icon: pixKeyTypeRandom,
            formatType: FormatPixType.RANDOM,
        },
    }), [t]);
    const getPixKeyDisplayInfo = useCallback((pixKey) => {
        const typeInfo = typeMap[pixKey.type] || typeMap[PixType.RANDOM];
        const formattedValue = pixKey.type === PixType.TAX_IDENTIFIER
            ? formatPixKeyAlphanumeric(pixKey.key, FormatPixType.TAX_IDENTIFIER, { isValidCnpj, maskCnpj, unmaskCnpj })
            : formatPixKey(pixKey.key, typeInfo.formatType);
        return {
            key: pixKey.key,
            type: pixKey.type,
            status: pixKey.status,
            displayValue: formattedValue,
            icon: typeInfo.icon,
            label: typeInfo.label,
        };
    }, [typeMap, isValidCnpj, maskCnpj, unmaskCnpj]);
    const handleCopyKey = useCallback((key) => {
        navigator.clipboard.writeText(key);
    }, []);
    const handleDeleteKey = useCallback(async (key) => {
        const success = await handleDeletePixKey(key);
        if (success) {
            await getPixKeys();
        }
        stopLoading();
        return success;
    }, [handleDeletePixKey, getPixKeys, stopLoading]);
    return {
        pixKeys,
        loading: loading || deleteLoading,
        error: error || deleteError,
        hasKeys,
        getPixKeys,
        getPixKeyDisplayInfo,
        handleCopyKey,
        handleDeleteKey,
    };
}
